/* eslint-disable no-bitwise */
import type { BerLength} from './types';
import type { KLV } from './klv';
import { calculateKagSize, calculateKlvTotalSize } from './klv';
import { MultiArrayBuffer } from './multiArrayBuffer';
import { Parser } from './parser';
import { ArraySerializerTarget, Serializer } from './serializer';

export type PartitionType = 'header' | 'footer' | 'body';

/// SMPTE 377 - Table 5 – Partition Pack
export interface PartitionPack {
  partitionType: PartitionType,
  closed: boolean,
  complete: boolean,
  majorVersion: number, //UInt16
  minorVersion: number,
  kagSize: number,
  thisPartition: number,
  previousPartition: number,
  footerPartition: number,
  headerByteCount: number,
  indexByteCount: number,
  indexSid: number,
  bodyOffset: number,
  bodySid: number,
  operationalPattern: string,
  essenceContainers: string[],
}


export function parsePartitionPack(klv: KLV): PartitionPack {
  const partitionType : PartitionType = getPartitionTypeFromKey(klv.key);

  const { closed, complete } = getCloseCompleteFromKey(klv.key);

  const source = new MultiArrayBuffer();
  source.push(klv.value);
  const parser = new Parser(source);
  const majorVersion : number = parser.readUInt16BE();

  const minorVersion : number = parser.readUInt16BE();
  const kagSize : number = parser.readUInt32BE();
  const thisPartition : number = parser.readUInt64BE();
  const previousPartition : number = parser.readUInt64BE();
  const footerPartition : number = parser.readUInt64BE();
  const headerByteCount : number = parser.readUInt64BE();
  const indexByteCount : number = parser.readUInt64BE();
  const indexSid : number = parser.readUInt32BE();
  const bodyOffset : number = parser.readUInt64BE();
  const bodySid : number = parser.readUInt32BE();
  const operationalPattern : string = parser.readUl();

  const essenceContainerCount : number = parser.readUInt32BE();
  const essenceContainerElementSize : number = parser.readUInt32BE();
  if (essenceContainerElementSize !== 16) {
    throw new Error(`Unexpected essenceContainerElementSize: ${essenceContainerElementSize}`);
  }
  const essenceContainers : string[] = [];
  for (let i = 0; i < essenceContainerCount; i++) {
    essenceContainers.push(parser.readUl());
  }

  if (parser.remain() !== 0) {
    throw new Error('Trailing unparsed bytes in partitionPack');
  }

  return {
    partitionType,
    closed,
    complete,
    majorVersion,
    minorVersion,
    kagSize,
    thisPartition,
    previousPartition,
    footerPartition,
    headerByteCount,
    indexByteCount,
    indexSid,
    bodyOffset,
    bodySid,
    operationalPattern,
    essenceContainers,
  };
}

export function serializePartitionPack(p: PartitionPack): KLV {
  let byte13;
  let byte14 = 0;
  switch (p.partitionType) {
    case 'header':
      byte13 = 2;
      break;
    case 'body':
      byte13 = 3;
      break;
    case 'footer':
      byte13 = 4;
      break;
  }

  if (p.closed) {
    byte14 |= 1;
  }
  if (p.complete) {
    byte14 |= 2;
  }
  byte14 += 1;

  const target = new ArraySerializerTarget();
  const serializer = new Serializer(target);
  // offset=0
  serializer.writeUInt16BE(p.majorVersion);
  // offset=2
  serializer.writeUInt16BE(p.minorVersion);
  // offset=4
  serializer.writeUInt32BE(p.kagSize);
  // offset=8
  serializer.writeUInt64BE(p.thisPartition);
  // offset=16
  serializer.writeUInt64BE(p.previousPartition);
  // offset=32
  serializer.writeUInt64BE(p.footerPartition);
  // offset=40
  serializer.writeUInt64BE(p.headerByteCount);
  // offset=48
  serializer.writeUInt64BE(p.indexByteCount);
  // offset=56
  serializer.writeUInt32BE(p.indexSid);
  // offset=60
  serializer.writeUInt64BE(p.bodyOffset);
  serializer.writeUInt32BE(p.bodySid);
  serializer.writeUl(p.operationalPattern);
  serializer.writeUInt32BE(p.essenceContainers.length);
  serializer.writeUInt32BE(16);
  for (const essenceContainer of p.essenceContainers) {
    serializer.writeUl(essenceContainer);
  }

  const value = target.getArray();
  const length : BerLength = { length: value.length, bytesUsed: 4 };
  const keyBytes = [0x06, 0x0e, 0x2b, 0x34, 0x02, 0x05, 0x01, 0x01, 0x0d, 0x01, 0x02, 0x01, 0x01, byte13, byte14, 0x00];
  const key = keyBytes.map((byte) => byte.toString(16).padStart(2, '0')).join('.');
  return { key, length, value };
}

export function calculatePartitionPackSize(partitionPack: PartitionPack): number {
  return calculateKlvTotalSize(serializePartitionPack(partitionPack));
}

export function calculatePaddedPartitionPackSize(partitionPack: PartitionPack): number {
  return calculateKagSize(calculatePartitionPackSize(partitionPack), partitionPack.kagSize);
}

// End of public API

// Key: ab.cd.ef.gh.01.23.45.67.ab.cd.ef.gh.76.54.21
function getKeyByte(key: string, index: number): number {
  const stringOffset = index * 3;
  return parseInt(key.substring(stringOffset, stringOffset + 2), 16);
}

function getPartitionTypeFromKey(key: string): PartitionType {
  const byte13 = getKeyByte(key, 13);
  switch (byte13) {
    case 2: return 'header';
    case 3: return 'body';
    case 4: return 'footer';
    default: throw new Error(`Unknown partition type: ${byte13}`);
  }
}

function getCloseCompleteFromKey(key: string): { closed: boolean, complete: boolean } {
  const byte13 = getKeyByte(key, 14);
  switch (byte13) {
    case 1: return { closed: false, complete: false };
    case 2: return { closed: true, complete: false };
    case 3: return { closed: false, complete: true };
    case 4: return { closed: true, complete: true };
    default: throw new Error(`Unknown close/complete state: ${byte13}`);
  }
}
